import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Translation resources
const resources = {
  en: {
    translation: {
      // Navigation
      "nav.home": "Home",
      "nav.marketplace": "Marketplace",
      "nav.suppliers": "Suppliers",
      "nav.rfq": "RFQ (Request for Quotation)",
      "nav.logistics": "Logistics",
      "nav.blog": "Insights",
      "nav.about": "About",
      "nav.contact": "Contact",
      "nav.login": "Login",
      "nav.register": "Register",
      "nav.dashboard": "Dashboard",
      "nav.logout": "Logout",
      
      // Hero
      "hero.headline": "Source from UAE. Sell across East Africa.",
      "hero.subheadline": "An AI-assisted B2B marketplace helping suppliers onboard faster, structure listings, and build trust across Africa–Gulf trade.",
      "hero.cta.browse": "Browse Suppliers",
      "hero.cta.rfq": "Request Quotation",
      
      // How it works
      "howItWorks.title": "How It Works",
      "howItWorks.overline": "SIMPLE 5-STEP PROCESS",
      "howItWorks.step1.title": "Create Account",
      "howItWorks.step1.desc": "AI guides suppliers through onboarding and verification requirements.",
      "howItWorks.step2.title": "Browse or List",
      "howItWorks.step2.desc": "AI helps structure product details for international buyers.",
      "howItWorks.step3.title": "Submit RFQ",
      "howItWorks.step3.desc": "AI assists buyers in drafting clear RFQs.",
      "howItWorks.step4.title": "Compare Bids",
      "howItWorks.step4.desc": "Review proposals from verified suppliers",
      "howItWorks.step5.title": "Secure Trade",
      "howItWorks.step5.desc": "Complete transaction with trade assurance",
      
      // Categories
      "categories.title": "Trade Categories",
      "categories.overline": "EXPLORE BY CATEGORY",
      
      // Featured Suppliers
      "suppliers.featured.title": "Featured Suppliers",
      "suppliers.featured.overline": "VERIFIED PARTNERS",
      "suppliers.verified": "Verified",
      "suppliers.viewProfile": "View Profile",
      
      // Testimonials
      "testimonials.title": "What Our Partners Say About Trading with AI Assistance",
      "testimonials.overline": "TESTIMONIALS",
      
      // FAQ
      "faq.title": "Frequently Asked Questions",
      "faq.overline": "FAQ",
      
      // Newsletter
      "newsletter.title": "Stay Updated",
      "newsletter.subtitle": "Get the latest trade insights, new suppliers, and market updates.",
      "newsletter.placeholder": "Enter your email",
      "newsletter.cta": "Subscribe",
      "newsletter.success": "Successfully subscribed!",
      
      // Footer
      "footer.tagline": "Bridging Gulf Excellence with African Growth",
      "footer.company": "Company",
      "footer.resources": "Resources",
      "footer.legal": "Legal",
      "footer.privacy": "Privacy Policy",
      "footer.terms": "Terms of Service",
      "footer.copyright": "© 2024 AfriGulf Market. All rights reserved.",
      
      // Marketplace
      "marketplace.title": "Marketplace",
      "marketplace.filters": "Filters",
      "marketplace.category": "Category",
      "marketplace.priceRange": "Price Range (USD)",
      "marketplace.moq": "MOQ (Minimum Order Quantity)",
      "marketplace.location": "Supplier Location",
      "marketplace.applyFilters": "Apply Filters",
      "marketplace.clearFilters": "Clear Filters",
      "marketplace.noProducts": "No products found",
      "marketplace.requestQuote": "Request Quote",
      
      // Product
      "product.specs": "Specifications",
      "product.moq": "MOQ (Minimum Order Quantity)",
      "product.leadTime": "Lead Time",
      "product.days": "days",
      "product.priceRange": "Price Range",
      "product.supplier": "Supplier",
      "product.location": "Location",
      
      // RFQ
      "rfq.title": "Request for Quotation",
      "rfq.create": "Create RFQ",
      "rfq.myRfqs": "My RFQs",
      "rfq.category": "Category",
      "rfq.specs": "Specifications",
      "rfq.quantity": "Quantity",
      "rfq.destination": "Destination",
      "rfq.submit": "Submit RFQ",
      "rfq.status.open": "Open",
      "rfq.status.closed": "Closed",
      "rfq.status.awarded": "Awarded",
      "rfq.viewBids": "View Bids",
      "rfq.noBids": "No bids yet",
      
      // Bids
      "bids.title": "Bids",
      "bids.submit": "Submit Bid",
      "bids.price": "Price (USD)",
      "bids.leadTime": "Lead Time (days)",
      "bids.message": "Message",
      "bids.accept": "Accept",
      "bids.reject": "Reject",
      "bids.pending": "Pending",
      "bids.accepted": "Accepted",
      "bids.rejected": "Rejected",
      
      // Logistics
      "logistics.title": "Logistics & Shipping",
      "logistics.overline": "UAE TO EAST AFRICA",
      "logistics.calculator.title": "Shipping Calculator",
      "logistics.calculator.origin": "Origin",
      "logistics.calculator.destination": "Destination",
      "logistics.calculator.weight": "Weight (kg)",
      "logistics.calculator.volume": "Volume (CBM)",
      "logistics.calculator.estimate": "Get Estimate",
      "logistics.result.cost": "Estimated Cost",
      "logistics.result.days": "Estimated Transit",
      "logistics.result.method": "Shipping Method",
      "logistics.lanes.title": "Trade Lanes",
      "logistics.lanes.desc": "Direct shipping routes from UAE ports to major East African destinations",
      
      // Trade Assurance
      "assurance.title": "Trade Assurance",
      "assurance.overline": "SECURE TRANSACTIONS",
      "assurance.escrow.title": "Escrow Protection",
      "assurance.escrow.desc": "Funds held securely until delivery confirmation",
      "assurance.verified.title": "Verified Suppliers",
      "assurance.verified.desc": "All suppliers undergo verification process",
      "assurance.dispute.title": "Dispute Resolution",
      "assurance.dispute.desc": "Professional mediation for any trade issues",
      
      // Blog
      "blog.title": "Trade Insights",
      "blog.overline": "LATEST ARTICLES",
      "blog.readMore": "Read More",
      "blog.backToList": "Back to Insights",
      
      // About
      "about.title": "About AfriGulf Market",
      "about.mission.title": "Our Mission",
      "about.mission.desc": "To democratize cross-border B2B trade between the Gulf and East Africa through technology, trust, and transparency.",
      "about.vision.title": "Our Vision",
      "about.vision.desc": "A seamlessly connected trade corridor where businesses of all sizes can thrive in international commerce.",
      "about.values.title": "Our Values",
      "about.team.title": "Our Team",
      "about.team.overline": "LEADERSHIP",
      
      // Contact
      "contact.title": "Contact Us",
      "contact.subtitle": "Have questions? We're here to help.",
      "contact.form.name": "Your Name",
      "contact.form.email": "Email Address",
      "contact.form.message": "Message",
      "contact.form.submit": "Send Message",
      "contact.form.success": "Message sent successfully!",
      "contact.whatsapp": "Chat on WhatsApp",
      "contact.office": "Our Office",
      
      // Auth
      "auth.login.title": "Welcome Back",
      "auth.login.subtitle": "Sign in to your account",
      "auth.register.title": "Create Account",
      "auth.register.subtitle": "Join AfriGulf Market",
      "auth.email": "Email Address",
      "auth.password": "Password",
      "auth.name": "Full Name",
      "auth.role": "I am a",
      "auth.role.buyer": "Buyer",
      "auth.role.supplier": "Supplier",
      "auth.login.cta": "Sign In",
      "auth.register.cta": "Create Account",
      "auth.noAccount": "Don't have an account?",
      "auth.hasAccount": "Already have an account?",
      "auth.loginLink": "Sign in",
      "auth.registerLink": "Register",
      
      // Dashboard
      "dashboard.title": "Dashboard",
      "dashboard.welcome": "Welcome back",
      "dashboard.buyer.rfqs": "My RFQs (Requests for Quotation)",
      "dashboard.buyer.bidsReceived": "Bids Received",
      "dashboard.supplier.products": "My Products",
      "dashboard.supplier.bids": "My Bids",
      "dashboard.supplier.rfqs": "Available RFQs",
      
      // Admin
      "admin.title": "Admin Dashboard",
      "admin.users": "Users",
      "admin.suppliers": "Suppliers",
      "admin.products": "Products",
      "admin.rfqs": "RFQs",
      "admin.blog": "Blog Posts",
      "admin.contacts": "Contact Messages",
      "admin.analytics": "Analytics",
      
      // Common
      "common.loading": "Loading...",
      "common.error": "An error occurred",
      "common.save": "Save",
      "common.cancel": "Cancel",
      "common.delete": "Delete",
      "common.edit": "Edit",
      "common.view": "View",
      "common.search": "Search",
      "common.filter": "Filter",
      "common.all": "All",
      "common.none": "None",
      "common.yes": "Yes",
      "common.no": "No",
      
      // Language
      "language.en": "English",
      "language.ar": "العربية",
      "language.ur": "اردو",
      "language.sw": "Kiswahili"
    }
  },
  ar: {
    translation: {
      // Navigation
      "nav.home": "الرئيسية",
      "nav.marketplace": "السوق",
      "nav.suppliers": "الموردون",
      "nav.rfq": "طلب عرض أسعار",
      "nav.logistics": "الخدمات اللوجستية",
      "nav.blog": "المدونة",
      "nav.about": "من نحن",
      "nav.contact": "اتصل بنا",
      "nav.login": "تسجيل الدخول",
      "nav.register": "التسجيل",
      "nav.dashboard": "لوحة التحكم",
      "nav.logout": "تسجيل الخروج",
      
      // Hero
      "hero.headline": "استورد من الإمارات. بِع في شرق أفريقيا.",
      "hero.subheadline": "منصة B2B الموثوقة التي تربط موردي الإمارات بمشتري شرق أفريقيا من خلال طلبات عروض الأسعار الآمنة والموردين المعتمدين وضمان التجارة.",
      "hero.cta.browse": "تصفح الموردين",
      "hero.cta.rfq": "طلب عرض أسعار",
      
      // How it works
      "howItWorks.title": "كيف يعمل",
      "howItWorks.overline": "عملية من 5 خطوات بسيطة",
      "howItWorks.step1.title": "إنشاء حساب",
      "howItWorks.step1.desc": "سجل كمشتري أو مورد في دقائق",
      "howItWorks.step2.title": "تصفح أو أدرج",
      "howItWorks.step2.desc": "استكشف المنتجات أو أدرج مخزونك",
      "howItWorks.step3.title": "أرسل طلب عرض أسعار",
      "howItWorks.step3.desc": "طلب عرض أسعار بمواصفاتك",
      "howItWorks.step4.title": "قارن العروض",
      "howItWorks.step4.desc": "راجع العروض من الموردين المعتمدين",
      "howItWorks.step5.title": "تجارة آمنة",
      "howItWorks.step5.desc": "أكمل المعاملة مع ضمان التجارة",
      
      // Categories
      "categories.title": "فئات التجارة",
      "categories.overline": "استكشف حسب الفئة",
      
      // Featured Suppliers
      "suppliers.featured.title": "الموردون المميزون",
      "suppliers.featured.overline": "شركاء معتمدون",
      "suppliers.verified": "معتمد",
      "suppliers.viewProfile": "عرض الملف",
      
      // Testimonials
      "testimonials.title": "ماذا يقول شركاؤنا",
      "testimonials.overline": "شهادات",
      
      // FAQ
      "faq.title": "الأسئلة الشائعة",
      "faq.overline": "الأسئلة المتكررة",
      
      // Newsletter
      "newsletter.title": "ابق على اطلاع",
      "newsletter.subtitle": "احصل على أحدث رؤى التجارة والموردين الجدد وتحديثات السوق.",
      "newsletter.placeholder": "أدخل بريدك الإلكتروني",
      "newsletter.cta": "اشترك",
      "newsletter.success": "تم الاشتراك بنجاح!",
      
      // Footer
      "footer.tagline": "ربط التميز الخليجي بالنمو الأفريقي",
      "footer.company": "الشركة",
      "footer.resources": "الموارد",
      "footer.legal": "قانوني",
      "footer.privacy": "سياسة الخصوصية",
      "footer.terms": "شروط الخدمة",
      "footer.copyright": "© 2024 جلف بريدج تريد. جميع الحقوق محفوظة.",
      
      // Common
      "common.loading": "جار التحميل...",
      "common.error": "حدث خطأ",
      "common.save": "حفظ",
      "common.cancel": "إلغاء",
      "common.delete": "حذف",
      "common.edit": "تعديل",
      "common.view": "عرض",
      "common.search": "بحث",
      "common.filter": "تصفية",
      "common.all": "الكل",
      
      // Language
      "language.en": "English",
      "language.ar": "العربية",
      "language.ur": "اردو",
      "language.sw": "Kiswahili"
    }
  },
  ur: {
    translation: {
      // Navigation
      "nav.home": "ہوم",
      "nav.marketplace": "مارکیٹ پلیس",
      "nav.suppliers": "سپلائرز",
      "nav.rfq": "قیمت کی درخواست",
      "nav.logistics": "لاجسٹکس",
      "nav.blog": "بلاگ",
      "nav.about": "ہمارے بارے میں",
      "nav.contact": "رابطہ کریں",
      "nav.login": "لاگ ان",
      "nav.register": "رجسٹر",
      "nav.dashboard": "ڈیش بورڈ",
      "nav.logout": "لاگ آؤٹ",
      
      // Hero
      "hero.headline": "متحدہ عرب امارات سے خریدیں۔ مشرقی افریقہ میں فروخت کریں۔",
      "hero.subheadline": "قابل اعتماد B2B مارکیٹ پلیس جو متحدہ عرب امارات کے سپلائرز کو مشرقی افریقی خریداروں سے محفوظ RFQs، تصدیق شدہ سپلائرز، اور تجارتی یقین دہانی کے ذریعے جوڑتا ہے۔",
      "hero.cta.browse": "سپلائرز دیکھیں",
      "hero.cta.rfq": "قیمت کی درخواست",
      
      // How it works
      "howItWorks.title": "یہ کیسے کام کرتا ہے",
      "howItWorks.overline": "سادہ 5 قدمی عمل",
      "howItWorks.step1.title": "اکاؤنٹ بنائیں",
      "howItWorks.step1.desc": "منٹوں میں خریدار یا سپلائر کے طور پر رجسٹر کریں",
      "howItWorks.step2.title": "براؤز یا لسٹ کریں",
      "howItWorks.step2.desc": "مصنوعات دیکھیں یا اپنا ذخیرہ لسٹ کریں",
      "howItWorks.step3.title": "RFQ جمع کروائیں",
      "howItWorks.step3.desc": "اپنی خصوصیات کے ساتھ قیمت کی درخواست",
      "howItWorks.step4.title": "بولیوں کا موازنہ کریں",
      "howItWorks.step4.desc": "تصدیق شدہ سپلائرز سے تجاویز کا جائزہ لیں",
      "howItWorks.step5.title": "محفوظ تجارت",
      "howItWorks.step5.desc": "تجارتی یقین دہانی کے ساتھ لین دین مکمل کریں",
      
      // Categories
      "categories.title": "تجارتی زمرے",
      "categories.overline": "زمرے کے مطابق دیکھیں",
      
      // Featured Suppliers
      "suppliers.featured.title": "نمایاں سپلائرز",
      "suppliers.featured.overline": "تصدیق شدہ پارٹنرز",
      "suppliers.verified": "تصدیق شدہ",
      "suppliers.viewProfile": "پروفائل دیکھیں",
      
      // Common
      "common.loading": "لوڈ ہو رہا ہے...",
      "common.error": "ایک خرابی پیش آگئی",
      
      // Language
      "language.en": "English",
      "language.ar": "العربية",
      "language.ur": "اردو",
      "language.sw": "Kiswahili"
    }
  },
  sw: {
    translation: {
      // Navigation
      "nav.home": "Nyumbani",
      "nav.marketplace": "Soko",
      "nav.suppliers": "Wasambazaji",
      "nav.rfq": "Ombi la Bei",
      "nav.logistics": "Usafirishaji",
      "nav.blog": "Blogu",
      "nav.about": "Kuhusu",
      "nav.contact": "Wasiliana",
      "nav.login": "Ingia",
      "nav.register": "Jisajili",
      "nav.dashboard": "Dashibodi",
      "nav.logout": "Toka",
      
      // Hero
      "hero.headline": "Nunua kutoka UAE. Uza Afrika Mashariki.",
      "hero.subheadline": "Jukwaa la B2B linaloaminiwa linaunganisha wasambazaji wa UAE na wanunuzi wa Afrika Mashariki kupitia RFQ salama, wasambazaji waliothibitishwa, na uhakikisho wa biashara.",
      "hero.cta.browse": "Angalia Wasambazaji",
      "hero.cta.rfq": "Omba Bei",
      
      // How it works
      "howItWorks.title": "Jinsi Inavyofanya Kazi",
      "howItWorks.overline": "MCHAKATO RAHISI WA HATUA 5",
      "howItWorks.step1.title": "Fungua Akaunti",
      "howItWorks.step1.desc": "Jisajili kama mnunuzi au msambazaji kwa dakika chache",
      "howItWorks.step2.title": "Tafuta au Orodhesha",
      "howItWorks.step2.desc": "Chunguza bidhaa au orodhesha bidhaa zako",
      "howItWorks.step3.title": "Tuma RFQ",
      "howItWorks.step3.desc": "Ombi la Bei na mahitaji yako",
      "howItWorks.step4.title": "Linganisha Zabuni",
      "howItWorks.step4.desc": "Kagua mapendekezo kutoka kwa wasambazaji waliothibitishwa",
      "howItWorks.step5.title": "Biashara Salama",
      "howItWorks.step5.desc": "Kamilisha shughuli na uhakikisho wa biashara",
      
      // Categories
      "categories.title": "Makundi ya Biashara",
      "categories.overline": "CHUNGUZA KWA KATEGORIA",
      
      // Featured Suppliers
      "suppliers.featured.title": "Wasambazaji Maarufu",
      "suppliers.featured.overline": "WASHIRIKA WALIOTHIBITISHWA",
      "suppliers.verified": "Imethibitishwa",
      "suppliers.viewProfile": "Tazama Wasifu",
      
      // Common
      "common.loading": "Inapakia...",
      "common.error": "Hitilafu ilitokea",
      
      // Language
      "language.en": "English",
      "language.ar": "العربية",
      "language.ur": "اردو",
      "language.sw": "Kiswahili"
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: false,
    interpolation: {
      escapeValue: false
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage']
    }
  });

export default i18n;

// RTL languages
export const RTL_LANGUAGES = ['ar', 'ur'];

export const isRTL = (language) => RTL_LANGUAGES.includes(language);

